import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { FileText, Plus } from "@fleet/icons";
import type { Page } from "@apunte-digital/powersync";

import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyPagesIllustration } from "@/components/ui/illustrations";
import { usePageTree } from "@/features/notes/hooks/use-page-tree";
import { usePageMutations } from "@/features/notes/hooks/use-page-mutations";

const RECENT_LIMIT = 8;

function recentPages(pages: Page[]): Page[] {
  return [...pages]
    .toSorted((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""))
    .slice(0, RECENT_LIMIT);
}

export function Dashboard() {
  const { t } = useI18n();
  const session = useSession();
  const { pages } = usePageTree();
  const mutations = usePageMutations();

  const firstName = (): string | undefined => session.data?.user.name?.split(" ")[0];
  const allPages = createMemo(() => pages.data ?? []);
  const recent = createMemo(() => recentPages(allPages()));
  const pageCount = createMemo(() => allPages().length);

  return (
    <div
      class="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 [scrollbar-gutter:stable]"
      style={{ "overflow-anchor": "none" }}
    >
      <div class="mx-auto max-w-5xl space-y-10">
        <header class="space-y-4">
          <h1 class="font-serif text-5xl font-semibold tracking-tight md:text-6xl">
            {/* oxlint-disable lingui/no-unlocalized-strings -- brand name */}
            Apunte Digital
            {/* oxlint-enable lingui/no-unlocalized-strings */}
          </h1>
          <Show
            when={session.isAuthenticated()}
            fallback={
              <p class="text-base text-muted-foreground max-w-md">
                {t(msg`You are in visitor mode. Sign in to sync your notes across devices.`)}
              </p>
            }
          >
            <div class="flex flex-col">
              <span class="text-lg font-medium text-foreground/80">
                {(() => {
                  const name = firstName() ?? t(msg`User`);
                  return t(msg`Hello, ${name}`);
                })()}
              </span>
              <span class="text-sm text-muted-foreground">{session.data?.user.email}</span>
            </div>
          </Show>
        </header>

        <section class="flex items-center gap-4">
          <div class="rounded-xl border border-border/50 bg-card/40 px-5 py-4">
            <div class="text-3xl font-bold">{pageCount()}</div>
            <div class="text-xs uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Pages`)}
            </div>
          </div>
          <Button
            class="gap-2"
            disabled={!mutations.ready()}
            onClick={() => {
              void mutations.handleCreatePage();
            }}
          >
            <Plus class="h-4 w-4" />
            {t(msg`New page`)}
          </Button>
        </section>

        <section class="space-y-3">
          <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground/70">
            {t(msg`Recent pages`)}
          </h2>
          <Show
            when={recent().length > 0}
            fallback={
              <EmptyState
                icon={FileText}
                illustration={<EmptyPagesIllustration />}
                title={t(msg`No pages yet`)}
                description={t(msg`Create your first page to start taking notes.`)}
                onIconActivate={
                  mutations.ready()
                    ? () => {
                        void mutations.handleCreatePage();
                      }
                    : undefined
                }
                iconActionLabel={t(msg`Create your first page`)}
              />
            }
          >
            <ul class="grid gap-2 sm:grid-cols-2">
              <For each={recent()}>
                {(page, index) => (
                  <li
                    class="motion-safe:animate-slide-in-up"
                    style={{
                      "animation-delay": `${Math.min(index() * 40, 280)}ms`,
                      "animation-fill-mode": "backwards",
                    }}
                  >
                    <Link
                      to="/notes/$pageId"
                      params={{ pageId: page.id }}
                      class="flex items-center gap-3 rounded-lg border border-border/40 bg-card/30 px-4 py-3 transition-[background-color,transform] motion-safe:will-change-transform hover:-translate-y-0.5 hover:bg-accent/10 active:scale-[0.98] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      <span class="text-lg">{page.icon ?? "📄"}</span>
                      <span class="truncate text-sm font-medium">
                        {page.title || t(msg`Untitled`)}
                      </span>
                    </Link>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </section>
      </div>
    </div>
  );
}
